import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Typography, Box, useMediaQuery } from '@mui/material';
import moment from 'moment';

import apis from '@src/apis';
import actions from '@src/redux/actions';
import { checkVietNam } from '@src/utils/checkCountry';
import {
  FREE_PACKAGE_CODES,
  PACKAGE_LEVEL,
  PACKAGE_STUDIO_VERSION,
  PACKAGE_TYPE,
} from '@src/constants/package';
import { PAYMENT_HUB_RENEWAL_TYPE } from '@src/constants/subscription';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import theme from '@src/styles/theme';
import useUserInfo from '@src/hooks/useTtsUser';
import SubscriptionItem from './SubscriptionItem';
import Dialog from './Dialog';
import { StyledSubscription, StyledSubscriptionV3 } from './index.style';
import SubscriptionItemV2V3 from './SubscriptionItemV2V3';
import SubscriptionItemV3 from './SubscriptionItemV3';
import SubscriptionItemV2 from './SubscriptionItemV2';
import SubscriptionCustom from './SubscriptionCustomItem';
import SubscriptionCustomAndV2Item from './SubscriptionCustomAndV2Item';

const DIALOG_NAME = {
  CANCEL_AUTO_RENEWAL: 'CANCEL_AUTO_RENEWAL',
  REGISTER_AUTO_RENEWAL: 'REGISTER_AUTO_RENEWAL',
};

const Subscription = () => {
  const [openDialog, setOpenDialog] = useState({});
  const [usingOrders, setUsingOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState({});
  const [lastUsingOrder, setLastUsingOrder] = useState({});

  const isVietNam = checkVietNam();
  const isMobile = useMediaQuery(theme.breakpoints.down('smd'));

  const { subscriptionPurchase } = useSelector((state) => state.user);
  const user = useSelector((state) => state.user);
  const { user: userInfo } = useSelector((state) => state.auth);
  const { preservationExpiryDate, packageExpiryDate } = userInfo;
  const { ttsUser } = useUserInfo();
  const { studio } = ttsUser;
  const { cycle, topUp, custom } = studio || {};

  const {
    capturedRolloverCredits: capturedRolloverCreditsCycle = 0,
    remainingCredits: remainingCycleCredits = 0,
    nextResetAt,
    purchasedTopUpCredits,
  } = cycle || {};

  const {
    remainingCredits: remainingTopUpCredits = 0,
    capturedRolloverCredits: capturedRolloverCreditsTopUp = 0,
  } = topUp || {};

  const { remainingCredits: remainingCustomCredits = 0 } = custom || {};

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleOpenDialog = (dialogName, orderInfo) => {
    setOpenDialog({ ...openDialog, [dialogName]: true });
    setSelectedOrder(orderInfo);
  };

  const { getFeatureValue } = useFeatureFlags();
  const showNewMyPackage = getFeatureValue(FEATURE_KEYS.NEW_MY_PACKAGE, {
    userId: userInfo.id,
    email: userInfo.email,
    phoneNumber: userInfo.phoneNumber,
  });

  const totalRemainingCredits = remainingCycleCredits + remainingTopUpCredits;

  const totalCapturedRolloverCredits =
    capturedRolloverCreditsCycle + capturedRolloverCreditsTopUp;

  const handleSetUsingOrders = (currUsingOrders) => {
    // Check saveUsingOrder have studio order
    const hasStudioOrder = currUsingOrders.find((order) => {
      if (!order?.package) return false;

      const isStudioType = order.package.type === PACKAGE_TYPE.STUDIO;
      const isNotExpired = moment(packageExpiryDate).isAfter(moment());
      const isAutoRenewOrder =
        order?.renewalType === PAYMENT_HUB_RENEWAL_TYPE.AUTO_RENEW;

      return isStudioType && (isNotExpired || isAutoRenewOrder);
    });

    let currPackages = currUsingOrders;

    if (!hasStudioOrder) {
      const { usingPackage } = user;
      const studioOrder = {
        package: usingPackage,
        packageExpiryDate: userInfo.packageExpiryDate,
        type: PACKAGE_TYPE.STUDIO,
      };
      currPackages = currUsingOrders.filter(
        (order) => order?.package?.type !== PACKAGE_TYPE.STUDIO,
      );

      // Add studio order to first of saveUsingOrders
      currPackages.unshift(studioOrder);
    }

    setUsingOrders(currPackages);
  };

  const fetchUsingOrders = async () => {
    const data = await apis.orders.getUsingOrders();
    if (data && data.status) {
      handleSetUsingOrders(data.result);
      setLastUsingOrder(data.result[0]);
    }
  };

  const handleCloseDialog = (dialogName) => {
    setOpenDialog({ ...openDialog, [dialogName]: false });
  };

  const handleCloseAutoRenewalDialog = () => {
    handleCloseDialog(DIALOG_NAME.CANCEL_AUTO_RENEWAL);
  };

  const handleConfirmCancelAutoRenew = async () => {
    const res = await apis.subscription.cancelSubscription();
    if (res?.status !== 1) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'cancelSubscriptionFail',
        }),
      );
    } else {
      dispatch(
        actions.noti.push({
          severity: 'success',
          message: 'cancelSubscriptionSuccess',
        }),
      );
      dispatch(actions.user.updateSubscriptionPurchase({}));
    }
    handleCloseAutoRenewalDialog();
  };

  const handleCloseRegisterAutoRenewalDialog = () => {
    handleCloseDialog(DIALOG_NAME.REGISTER_AUTO_RENEWAL);
  };

  useEffect(() => {
    fetchUsingOrders();
  }, [user?.usingPackage]);

  const renderSubscriptionItem = (usingOrder) => {
    if (showNewMyPackage) {
      const isPackageV3 =
        usingOrder?.type === PACKAGE_TYPE.STUDIO &&
        usingOrder.package.version === PACKAGE_STUDIO_VERSION.V3;

      const isCustomPackage =
        usingOrder?.type === PACKAGE_TYPE.STUDIO &&
        usingOrder.package.level === PACKAGE_LEVEL.CUSTOM;

      const hasV2Package = Boolean(userInfo?.remainingCharacters);
      const packageCode = usingOrder?.package?.code;
      const isFreePackage = FREE_PACKAGE_CODES.includes(packageCode);
      const isDubbingPackage = usingOrder?.type === PACKAGE_TYPE.DUBBING;
      const isApiPackage = usingOrder?.type === PACKAGE_TYPE.API;

      if (isCustomPackage && hasV2Package)
        return (
          <SubscriptionCustomAndV2Item
            packageInfo={usingOrder?.package}
            usingOrder={usingOrder}
            totalRemainingCredits={remainingCustomCredits}
            isMobile={isMobile}
            lastUsingOrder={lastUsingOrder}
          />
        );

      if (isCustomPackage && !isFreePackage)
        return (
          <SubscriptionCustom
            packageInfo={usingOrder?.package}
            usingOrder={usingOrder}
            hasV2Package={hasV2Package}
            totalRemainingCredits={remainingCustomCredits}
            isMobile={isMobile}
            lastUsingOrder={lastUsingOrder}
          />
        );

      if (isPackageV3 && hasV2Package) {
        return (
          <SubscriptionItemV2V3
            packageInfo={usingOrder?.package}
            usingOrder={usingOrder}
            hasV2Package={hasV2Package}
            totalRemainingCredits={totalRemainingCredits}
            nextResetAt={nextResetAt}
            purchasedTopUpCredits={purchasedTopUpCredits}
            isMobile={isMobile}
            subscriptionPurchase={subscriptionPurchase}
            totalCapturedRolloverCredits={totalCapturedRolloverCredits}
          />
        );
      }

      if (isPackageV3 && !isFreePackage) {
        return (
          <SubscriptionItemV3
            packageInfo={usingOrder?.package}
            usingOrder={usingOrder}
            totalRemainingCredits={totalRemainingCredits}
            purchasedTopUpCredits={purchasedTopUpCredits}
            isMobile={isMobile}
            nextResetAt={nextResetAt}
            subscriptionPurchase={subscriptionPurchase}
            totalCapturedRolloverCredits={totalCapturedRolloverCredits}
          />
        );
      }

      if (isDubbingPackage) return '';

      return (
        <SubscriptionItemV2
          packageInfo={usingOrder?.package}
          usingOrder={usingOrder}
          isMobile={isMobile}
          isFreePackage={isFreePackage}
          isApiPackage={isApiPackage}
          preservationExpiryDate={preservationExpiryDate}
          subscriptionPurchase={subscriptionPurchase}
          lastUsingOrder={lastUsingOrder}
        />
      );
    }

    return (
      <SubscriptionItem
        packageInfo={usingOrder?.package}
        usingOrder={usingOrder}
        isVietNam={isVietNam}
        subscriptionPurchase={subscriptionPurchase}
        handleOpenDialog={handleOpenDialog}
        dialogName={DIALOG_NAME}
      />
    );
  };

  if (showNewMyPackage) {
    return (
      <StyledSubscriptionV3>
        <div className="header-payment">
          <Typography className="title">{t('myPackages')}</Typography>
        </div>
        <Box className="using-package-list">
          {usingOrders.map((usingOrder) => (
            <Box key={usingOrder.id}>{renderSubscriptionItem(usingOrder)}</Box>
          ))}
        </Box>
        <Dialog
          dialogName={DIALOG_NAME}
          openDialog={openDialog}
          selectedOrder={selectedOrder}
          handleCloseAutoRenewalDialog={handleCloseAutoRenewalDialog}
          handleConfirmCancelAutoRenew={handleConfirmCancelAutoRenew}
          handleCloseRegisterAutoRenewalDialog={
            handleCloseRegisterAutoRenewalDialog
          }
        />
      </StyledSubscriptionV3>
    );
  }

  return (
    <StyledSubscription>
      <div className="header-payment">
        <Typography className="title">{t('myPackages')}</Typography>
      </div>
      <Box className="using-package-list">
        {usingOrders.map((usingOrder) => (
          <Box key={usingOrder.id}>{renderSubscriptionItem(usingOrder)}</Box>
        ))}
      </Box>
      <Dialog
        dialogName={DIALOG_NAME}
        openDialog={openDialog}
        selectedOrder={selectedOrder}
        handleCloseAutoRenewalDialog={handleCloseAutoRenewalDialog}
        handleConfirmCancelAutoRenew={handleConfirmCancelAutoRenew}
        handleCloseRegisterAutoRenewalDialog={
          handleCloseRegisterAutoRenewalDialog
        }
      />
    </StyledSubscription>
  );
};

export default Subscription;
